import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowDownRight, ArrowUpRight, BarChart3, Wallet } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Cash-flow widget for the assistant panel (BUG-87 AC2).
 *
 * The router emits `type: 'cash-flow-chart'` alongside the seeded cash-position
 * answer, but `UnifiedWidgetRenderer` had no case for it, so the demo's
 * headline answer rendered a grey "CASH FLOW-CHART" placeholder instead of the
 * numbers behind the sentence.
 *
 * Every value arrives ALREADY FORMATTED from `DemoIntentService` — the same
 * deterministic figures the answer text quotes and the dashboard cards read
 * (PRD-38's guarantee). This component therefore never recomputes or reformats
 * a figure; it only lays the given strings out. The bar widths are derived from
 * the strings purely for proportion and are decorative.
 */

interface CashFlowChartData {
  /** Formatted, e.g. "$26,299,454" */
  workingCapital: string;
  receivablesTotal: string;
  payablesTotal: string;
  netPosition: string;
  overdueAmount: string;
}

interface CashFlowChartWidgetProps {
  data: CashFlowChartData;
  onAction?: (action: string, data?: any) => void;
}

/** Proportion only — never used to display a figure. */
const magnitude = (formatted?: string): number => {
  const n = Number(String(formatted ?? '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? Math.abs(n) : 0;
};

export const CashFlowChartWidget = ({ data, onAction }: CashFlowChartWidgetProps) => {
  const { t } = useTranslation();

  const rows = [
    {
      key: 'receivables',
      label: t('chat.cashFlowWidget.receivables', 'Receivables'),
      value: data.receivablesTotal,
      tone: 'bg-success',
      Icon: ArrowUpRight,
    },
    {
      key: 'payables',
      label: t('chat.cashFlowWidget.payables', 'Payables'),
      value: data.payablesTotal,
      tone: 'bg-destructive',
      Icon: ArrowDownRight,
    },
    {
      key: 'overdue',
      label: t('chat.cashFlowWidget.overdue', 'Overdue'),
      value: data.overdueAmount,
      tone: 'bg-warning',
      Icon: ArrowDownRight,
    },
  ].filter((r) => r.value != null);

  const scale = Math.max(...rows.map((r) => magnitude(r.value)), 1);

  return (
    <div className="p-3 space-y-3">
      <Card className="border-0 shadow-none bg-muted">
        <CardContent className="p-3">
          <div className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-primary" />
            <span className="text-xs text-muted-foreground">
              {t('chat.cashFlowWidget.position', 'Cash position')}
            </span>
          </div>
          <p className="text-xl font-bold text-foreground mt-1">{data.workingCapital}</p>
          {data.netPosition && (
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('chat.cashFlowWidget.net', 'Net position')}: {data.netPosition}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="space-y-2">
        {rows.map(({ key, label, value, tone, Icon }) => (
          <div key={key}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Icon className="h-3 w-3" />
                {label}
              </span>
              <span className="text-xs font-semibold text-foreground">{value}</span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full ${tone}`}
                style={{ width: `${Math.round((magnitude(value) / scale) * 100)}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      <Button
        variant="outline"
        size="sm"
        className="w-full text-xs"
        onClick={() => onAction?.('view-scenario', data)}
      >
        <BarChart3 className="h-3 w-3 mr-1" />
        {t('chat.cashFlowWidget.viewDetail', 'View cash flow detail')}
      </Button>
    </div>
  );
};
